import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { useAppStore } from "../../../stores/RootStore";
import RoleRequire from "../../../helpers/auth/RoleRequire";
import { Separator } from "../../../ui/Separator";
import { DataTable } from "../../../ui/DataTable/DataTable";
import { Log, PageMeta, UserCompact } from "../../../types/types";
import { DateTime } from "luxon";
import { useFetch } from "../../../hooks/useFetch";
import Spinner from "../../../ui/Spinner/Spinner";
import LogsPagination from "./Pagination";
import { FormProvider, useForm } from "react-hook-form";
import LogsFilters from "./Filters";
import { SimpleDataTable } from "../../../ui/DataTable/SimpleDataTable";

export const Logs = () => {
  const { t, i18n } = useTranslation(["translation", "logs"]);
  // Получение данных и обновление их в сторе.
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const methods = useForm({
    defaultValues: {
      dates: {
        from: undefined,
        to: undefined,
      },
      users: [],
      actions: [],
    },
  });
  const { handleSubmit, control, watch } = methods;

  const [params, setParams] = useState(new URLSearchParams({}));

  const generateParams = (formData: any) => {
    let newUsageParams = new URLSearchParams({});
    //newUsageParams.append("page", String(page));
    //newUsageParams.append("per", "40");

    if (formData.dates?.from) newUsageParams.append("from", formData.dates?.from?.toISOString()!);
    if (formData.dates?.to) newUsageParams.append("to", formData.dates?.to?.toISOString()!);

    if (formData.users) {
      for (let el of formData.users) {
        newUsageParams.append("user_ids[]", el.value);
      }
    }

    if (formData.actions) {
      for (let el of formData.actions) {
        newUsageParams.append("actions[]", el.value);
      }
    }

    setParams(newUsageParams);
    return newUsageParams;
  };

  const [{ response, isLoading, error }, doFetchItem] = useFetch(
    `/directories/logs?page=${page}&sort=created_at&direction=desc&${params.toString()}`
  );
  const [logs, setLogs] = useState<Log[]>();
  const [meta, setMeta] = useState<PageMeta>();

  const [{ response: usersDataResponse, isLoading: usersLoading, error: usersError }, doFetchUsers] =
    useFetch(`/users`);
  
  const [users, setUsers] = useState<UserCompact[]>();

  const [usersFilter, setUsersFilter] = useState<{ value: string; label: string }[]>([]);

  const [actionsFilter, setActionsFilter] = useState<{ value: string; label: string }[]>([]);

  const actionsList = [
    { value: "index", label: t("index", { ns: "logs" }) },
    { value: "usage_data_by_item", label: t("usage_data_by_item", { ns: "logs" }) },
    { value: "create", label: t("create", { ns: "logs" }) },
    { value: "update", label: t("update", { ns: "logs" }) },
    { value: "delete", label: t("delete", { ns: "logs" }) },
    { value: "collection_usage", label: t("collection_usage", { ns: "logs" }) },
    { value: "timelines", label: t("timelines", { ns: "logs" }) },
    { value: "show", label: t("show", { ns: "logs" }) },
    { value: "received_data", label: t("received_data", { ns: "logs" }) },
    { value: "member_power_usage", label: t("member_power_usage", { ns: "logs" }) },
    { value: "member_personnel_usage", label: t("member_personnel_usage", { ns: "logs" }) },
    { value: "member_usage", label: t("member_usage", { ns: "logs" }) },
    { value: "destroy", label: t("destroy", { ns: "logs" }) },
    { value: "daily_usage", label: t("daily_usage", { ns: "logs" }) },
    { value: "events", label: t("events", { ns: "logs" }) },
    { value: "usage_dataset", label: t("usage_dataset", { ns: "logs" }) },
    { value: "filter", label: t("filter", { ns: "logs" }) },
    { value: "sensor_type", label: t("sensor_type", { ns: "logs" }) },
    { value: "usage_dataset_by_personnels", label: t("usage_dataset_by_personnels", { ns: "logs" }) },
    { value: "events_by_personnels", label: t("events_by_personnels", { ns: "logs" }) },
  ];

  const divisions = useAppStore((store) => store.divisions);

  useEffect(() => {
    setLoading(true);
    doFetchItem();
    doFetchUsers();
  }, [params, page]);

  useEffect(() => {
    if (!response) {
      return;
    }
    setLogs(response.data as unknown as Log[]);
    setMeta(response.meta as unknown as PageMeta);
    setLoading(false);
  }, [response]);

  useEffect(() => {
    if (!usersDataResponse) return
    let usersData = usersDataResponse.data as unknown as UserCompact[];
    console.log(usersData);
    let userList = usersData.map(user =>{ return {
      value: String(user.attributes.user_id),
      label: user.attributes.middle_name
        ? `${user.attributes.last_name} ${user.attributes.middle_name} ${user.attributes.first_name}`
        : `${user.attributes.last_name} ${user.attributes.first_name}`,
    };})
    setUsersFilter(userList);
  }, [usersDataResponse]);

  let nodes = logs?.map((data) => {
    return {
      id: data.id,
      action: data.attributes.action,
      controller: data.attributes.controller,
      created_at: DateTime.fromISO(data.attributes.created_at),
      url: data.attributes.url,
      user_id: data.attributes.user_id,
      user_name: data.attributes.user_name,
    };
  });

  // Сортировка по умолчанию
  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const onSubmit = (formData: any) => {
    setPage(1);
    generateParams(formData).toString()
  };

  const columns: ColumnDef<any>[] = [
    {
      header: t("date"),
      size: 150,
      maxSize: 150,
      minSize: 150,
      cell: ({ row }) => row.original.created_at.toLocaleString(DateTime.DATETIME_SHORT),
    },
    {
      header: t("action"),
      size: 400,
      maxSize: 400,
      minSize: 400,
      cell: ({ row }) => (
        <div>
          <div className="font-medium truncate">{t(row.original.action, { ns: "logs" })}</div>
          <div className="font-medium truncate">{t(row.original.controller, { ns: "logs" })}</div>
        </div>
      ),
    },
    {
      header: t("participant"),
      size: 200,
      maxSize: 200,
      minSize: 200,
      cell: ({ row }) => row.original.user_name,
    },
  ];

  return loading ? (
    <Spinner />
  ) : (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background px-6">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('logs')}</span>
        </div>
      </div>
      <div className="flex-none px-6"> 
        <FormProvider {...methods}>
          <LogsFilters usersList={usersFilter} actionsList={actionsList} onSubmit={onSubmit} />
        </FormProvider>
      </div>
      <SimpleDataTable data={data.nodes} columns={columns} />
      <div className="h-14 flex-none flex items-center">
        <LogsPagination page={page} setPage={setPage} meta={meta} />
      </div>
    </div>
  );
};
